import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';
import { ArrowLeft, ArrowRight, Check, Upload, User, Briefcase, HelpCircle, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlumniFeedback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Section 1
    full_name: '',
    batch_number: '',
    headshot_url: '',
    course_feedback: '',
    consent_wall_of_fame: true,
    // Section 2
    career_status: '',
    // Section 3
    organization_name: '',
    role_title: '',
    display_role_publicly: false,
    wants_video_review: false,
    video_url: '',
    // Section 4
    future_outlook: '',
    support_needed: ''
  });

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const handleHeadshotUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast({ title: "Error", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Error", description: "File size must be less than 10 MB", variant: "destructive" });
      return;
    }
    
    setUploadingHeadshot(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', uploadPreset);
      formDataUpload.append('folder', 'jurisgo/alumni/headshots');
      
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        formDataUpload
      );
      
      setFormData({ ...formData, headshot_url: res.data.secure_url });
      toast({ title: "Photo Uploaded", description: "Your headshot has been uploaded successfully" });
    } catch (error) {
      toast({ title: "Upload Failed", description: "Failed to upload photo", variant: "destructive" });
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      toast({ title: "Error", description: "Please upload a video file", variant: "destructive" });
      return;
    }
    
    setUploadingVideo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', uploadPreset);
      formDataUpload.append('folder', 'jurisgo/alumni/videos');
      
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
        formDataUpload,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('Video upload progress:', progress);
          }
        }
      );
      
      setFormData({ ...formData, video_url: res.data.secure_url });
      toast({ title: "Video Uploaded", description: "Your video review has been uploaded" });
    } catch (error) {
      toast({ title: "Upload Failed", description: "Failed to upload video", variant: "destructive" });
    } finally {
      setUploadingVideo(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.full_name.trim()) newErrors.full_name = "Full name is required";
      if (!formData.batch_number) newErrors.batch_number = "Please select your batch";
      if (!formData.course_feedback.trim()) {
        newErrors.course_feedback = "Please share your feedback";
      } else if (formData.course_feedback.trim().length < 100) {
        newErrors.course_feedback = "Please write at least 100 characters";
      }
    }
    
    if (step === 2) {
      if (!formData.career_status) newErrors.career_status = "Please select an option";
    }
    
    if (step === 3) {
      if (!formData.role_title.trim()) newErrors.role_title = "Role title is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) return;
    
    if (currentStep === 2) {
      if (formData.career_status === 'not_yet') {
        setCurrentStep(4);
      } else {
        setCurrentStep(3);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep === 4) {
      setCurrentStep(2);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (currentStep === 3 && !validateStep(3)) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/alumni-feedback`, formData);
      setIsSubmitted(true);
      toast({ title: "Success!", description: "Your feedback has been submitted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit feedback", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalSteps = () => {
    if (formData.career_status === 'not_yet') return 3;
    if (formData.career_status === 'secured' || formData.career_status === 'interviewed') return 3;
    return 2;
  };

  const getCurrentStepNumber = () => {
    if (currentStep === 4) return 3;
    return currentStep;
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#f7f4ec]">
        {/* Header */}
        <header className="bg-[#14202e] py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src={mockData.logo} alt="JurisGo" className="h-8 w-8" />
              <span className="text-xl font-serif text-[#f7f4ec]">JurisGo</span>
            </Link>
          </div>
        </header>
        
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-[#9c7a3c] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-serif text-[#14202e] mb-4">Thank You!</h1>
          <p className="text-lg text-[#14202e]/70 mb-6">
            Your feedback has been successfully submitted.
          </p>
          {formData.consent_wall_of_fame && (
            <div className="bg-[#14202e]/5 border border-[#9c7a3c]/30 rounded-lg p-4 mb-6">
              <p className="text-[#14202e]">
                Your story is now live on our <strong>Wall of Fame</strong>! Thank you for sharing your journey with our community.
              </p>
            </div>
          )}
          <div className="flex gap-4 justify-center">
            <Button 
              onClick={() => navigate('/wall-of-fame')}
              className="bg-[#9c7a3c] hover:bg-[#8a6b35] text-white"
            >
              View Wall of Fame
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="border-[#14202e] text-[#14202e]"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f4ec]">
      {/* Header */}
      <header className="bg-[#14202e] py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img src={mockData.logo} alt="JurisGo" className="h-8 w-8" />
            <span className="text-xl font-serif text-[#f7f4ec]">JurisGo</span>
          </Link>
          <Link to="/" className="text-[#f7f4ec]/70 hover:text-[#f7f4ec] text-sm flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-[#14202e]/10 py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs text-[#9c7a3c] uppercase tracking-wider">
              Section {String(getCurrentStepNumber()).padStart(2, '0')} of {String(getTotalSteps()).padStart(2, '0')}
            </span>
            <span className="text-sm text-[#14202e]/60">
              {Math.round((getCurrentStepNumber() / getTotalSteps()) * 100)}% Complete
            </span>
          </div>
          <div className="h-1.5 bg-[#14202e]/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#9c7a3c] transition-all duration-500 rounded-full"
              style={{ width: `${(getCurrentStepNumber() / getTotalSteps()) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-serif text-[#14202e] mb-3">
            Alumni Feedback & Placement Audit
          </h1>
          <p className="text-[#14202e]/60">
            Share your JurisGo journey and help inspire future paralegals
          </p>
        </div>

        <Card className="border-0 shadow-xl bg-white">
          <CardContent className="p-8">
            {/* Section 1 - Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#14202e]/10">
                  <div className="w-10 h-10 bg-[#14202e] rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-[#f7f4ec]" />
                  </div>
                  <div>
                    <span className="font-mono text-xs text-[#9c7a3c] uppercase tracking-wider">Section 01</span>
                    <h2 className="text-xl font-serif text-[#14202e]">Basic Information & Marketing Assets</h2>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">Full Name *</label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Enter your full name"
                    className={`border-[#14202e]/20 focus:border-[#9c7a3c] ${errors.full_name ? 'border-red-500' : ''}`}
                  />
                  {errors.full_name && <p className="text-red-500 text-sm">{errors.full_name}</p>}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">Batch Number / Month of Enrollment *</label>
                  <select
                    value={formData.batch_number}
                    onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#9c7a3c] ${errors.batch_number ? 'border-red-500' : 'border-[#14202e]/20'}`}
                  >
                    <option value="">Select your batch...</option>
                    <option value="Certificate Session on Client Intake">Certificate Session on Client Intake</option>
                    <option value="Batch 1">Batch 1</option>
                    <option value="Batch 2">Batch 2</option>
                    <option value="Batch 3">Batch 3</option>
                    <option value="Batch 4">Batch 4</option>
                    <option value="Batch 5">Batch 5</option>
                  </select>
                  {errors.batch_number && <p className="text-red-500 text-sm">{errors.batch_number}</p>}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">Professional Headshot <span className="text-xs text-[#14202e]/50 font-normal">(Optional)</span></label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.headshot_url ? 'border-red-500' : 'border-[#14202e]/20'} ${formData.headshot_url ? 'border-[#9c7a3c] bg-[#9c7a3c]/5' : ''}`}>
                    {formData.headshot_url ? (
                      <div className="space-y-3">
                        <img src={formData.headshot_url} alt="Headshot" className="w-24 h-24 rounded-full object-cover mx-auto" />
                        <p className="text-sm text-[#9c7a3c]">Photo uploaded successfully</p>
                        <label className="cursor-pointer text-sm text-[#14202e]/60 hover:text-[#14202e] underline">
                          Change photo
                          <input type="file" accept="image/*" onChange={handleHeadshotUpload} className="hidden" />
                        </label>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="h-10 w-10 text-[#14202e]/30 mx-auto mb-3" />
                        <span className="text-[#14202e]/60 block mb-1">
                          {uploadingHeadshot ? 'Uploading...' : 'Click to upload your headshot'}
                        </span>
                        <span className="text-xs text-[#14202e]/40">Image only, max 10MB</span>
                        <input type="file" accept="image/*" onChange={handleHeadshotUpload} className="hidden" disabled={uploadingHeadshot} />
                      </label>
                    )}
                  </div>
                  {errors.headshot_url && <p className="text-red-500 text-sm">{errors.headshot_url}</p>}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">Course Feedback & Review *</label>
                  <p className="text-xs text-[#14202e]/60 mb-2">
                    Share your honest experience with JurisGo. How did the training help you handle U.S./California paralegal workflows (like demand letters or medical chronologies)? What did you love most about our curriculum?
                  </p>
                  <Textarea
                    value={formData.course_feedback}
                    onChange={(e) => setFormData({ ...formData, course_feedback: e.target.value })}
                    placeholder="Write your detailed feedback here (minimum 100 characters)..."
                    rows={5}
                    className={`border-[#14202e]/20 focus:border-[#9c7a3c] ${errors.course_feedback ? 'border-red-500' : ''}`}
                  />
                  <div className="flex justify-between text-xs">
                    {errors.course_feedback ? (
                      <p className="text-red-500">{errors.course_feedback}</p>
                    ) : (
                      <span className="text-[#14202e]/40">Minimum 100 characters</span>
                    )}
                    <span className={`${formData.course_feedback.length >= 100 ? 'text-[#9c7a3c]' : 'text-[#14202e]/40'}`}>
                      {formData.course_feedback.length} characters
                    </span>
                  </div>
                </div>

                <div className="bg-[#f7f4ec] rounded-lg p-4 border border-[#9c7a3c]/20">
                  <p className="text-sm text-[#14202e]">
                    By submitting, your name, photo, and review may be featured on JurisGo's <strong>Wall of Fame</strong> to inspire future paralegals.
                  </p>
                </div>
              </div>
            )}

            {/* Section 2 - Career Impact */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#14202e]/10">
                  <div className="w-10 h-10 bg-[#14202e] rounded-full flex items-center justify-center">
                    <Briefcase className="h-5 w-5 text-[#f7f4ec]" />
                  </div>
                  <div>
                    <span className="font-mono text-xs text-[#9c7a3c] uppercase tracking-wider">Section 02</span>
                    <h2 className="text-xl font-serif text-[#14202e]">Career Impact & Placement Auditing</h2>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e] mb-4">
                    Have you secured a job, internship, or freelancing opportunity since joining JurisGo, or have you been interviewed? *
                  </label>
                  
                  <div className="space-y-3">
                    {[
                      { value: 'secured', label: 'Yes, I secured a job/internship/freelance role' },
                      { value: 'interviewed', label: 'Yes, I have been interviewed (process ongoing)' },
                      { value: 'not_yet', label: 'No, not yet' }
                    ].map((option) => (
                      <label 
                        key={option.value}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.career_status === option.value 
                            ? 'border-[#9c7a3c] bg-[#9c7a3c]/5' 
                            : 'border-[#14202e]/10 hover:border-[#14202e]/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="career_status"
                          value={option.value}
                          checked={formData.career_status === option.value}
                          onChange={(e) => setFormData({ ...formData, career_status: e.target.value })}
                          className="w-5 h-5 text-[#9c7a3c] border-[#14202e]/30 focus:ring-[#9c7a3c]"
                        />
                        <span className="text-[#14202e]">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.career_status && <p className="text-red-500 text-sm mt-2">{errors.career_status}</p>}
                </div>
              </div>
            )}

            {/* Section 3 - Success Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#14202e]/10">
                  <div className="w-10 h-10 bg-[#9c7a3c] rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <span className="font-mono text-xs text-[#9c7a3c] uppercase tracking-wider">Section 03</span>
                    <h2 className="text-xl font-serif text-[#14202e]">Success Details</h2>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">
                    Name of the Law Firm / Organization / Client
                    <span className="text-xs text-[#14202e]/50 ml-2">(Optional, Private - never shown publicly)</span>
                  </label>
                  <Input
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    placeholder="e.g., Smith & Associates Law Firm"
                    className="border-[#14202e]/20 focus:border-[#9c7a3c]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">Role Title *</label>
                  <Input
                    value={formData.role_title}
                    onChange={(e) => setFormData({ ...formData, role_title: e.target.value })}
                    placeholder="e.g., Remote Personal Injury Paralegal, Legal Intern"
                    className={`border-[#14202e]/20 focus:border-[#9c7a3c] ${errors.role_title ? 'border-red-500' : ''}`}
                  />
                  {errors.role_title && <p className="text-red-500 text-sm">{errors.role_title}</p>}
                  
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.display_role_publicly}
                      onChange={(e) => setFormData({ ...formData, display_role_publicly: e.target.checked })}
                      className="w-4 h-4 text-[#9c7a3c] rounded border-[#14202e]/30 focus:ring-[#9c7a3c]"
                    />
                    <span className="text-sm text-[#14202e]/70">
                      Okay to display my role title publicly on the Wall of Fame (e.g. "Now working as a Remote Paralegal")
                    </span>
                  </label>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">Would you like to share a short video review?</label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer ${formData.wants_video_review ? 'border-[#9c7a3c] bg-[#9c7a3c]/5' : 'border-[#14202e]/10'}`}>
                      <input
                        type="radio"
                        checked={formData.wants_video_review === true}
                        onChange={() => setFormData({ ...formData, wants_video_review: true })}
                        className="w-4 h-4 text-[#9c7a3c]"
                      />
                      <span className="text-sm">Yes, I'll upload a video</span>
                    </label>
                    <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer ${formData.wants_video_review === false ? 'border-[#9c7a3c] bg-[#9c7a3c]/5' : 'border-[#14202e]/10'}`}>
                      <input
                        type="radio"
                        checked={formData.wants_video_review === false}
                        onChange={() => setFormData({ ...formData, wants_video_review: false })}
                        className="w-4 h-4 text-[#9c7a3c]"
                      />
                      <span className="text-sm">No, I'll stick to text</span>
                    </label>
                  </div>
                </div>

                {formData.wants_video_review && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[#14202e]">Upload your video review (maximum 1 minute)</label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${formData.video_url ? 'border-[#9c7a3c] bg-[#9c7a3c]/5' : 'border-[#14202e]/20'}`}>
                      {formData.video_url ? (
                        <div className="space-y-2">
                          <Check className="h-8 w-8 text-[#9c7a3c] mx-auto" />
                          <p className="text-sm text-[#9c7a3c]">Video uploaded successfully</p>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="h-10 w-10 text-[#14202e]/30 mx-auto mb-3" />
                          <span className="text-[#14202e]/60 block mb-1">
                            {uploadingVideo ? 'Uploading video...' : 'Click to upload your video'}
                          </span>
                          <span className="text-xs text-[#14202e]/40">Video files only</span>
                          <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" disabled={uploadingVideo} />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-[#14202e]/50">Note: Video reviews are admin-only until manually approved for publishing.</p>
                  </div>
                )}
              </div>
            )}

            {/* Section 4 - Future Outlook */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#14202e]/10">
                  <div className="w-10 h-10 bg-[#14202e] rounded-full flex items-center justify-center">
                    <HelpCircle className="h-5 w-5 text-[#f7f4ec]" />
                  </div>
                  <div>
                    <span className="font-mono text-xs text-[#9c7a3c] uppercase tracking-wider">Section 03</span>
                    <h2 className="text-xl font-serif text-[#14202e]">Future Outlook & Pipeline Support</h2>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e] mb-4">
                    Do you expect a relevant job or internship opportunity to come through in the next 3 months? *
                    <span className="text-xs text-[#14202e]/50 block mt-1">(Private - only visible to admin)</span>
                  </label>
                  
                  <div className="space-y-3">
                    {[
                      { value: 'confident', label: 'Yes, I feel confident with my current applications/skills' },
                      { value: 'need_guidance', label: 'Maybe, but I need more leads/guidance' },
                      { value: 'struggling', label: 'No, I am struggling to find openings' }
                    ].map((option) => (
                      <label 
                        key={option.value}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.future_outlook === option.value 
                            ? 'border-[#9c7a3c] bg-[#9c7a3c]/5' 
                            : 'border-[#14202e]/10 hover:border-[#14202e]/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="future_outlook"
                          value={option.value}
                          checked={formData.future_outlook === option.value}
                          onChange={(e) => setFormData({ ...formData, future_outlook: e.target.value })}
                          className="w-5 h-5 text-[#9c7a3c] border-[#14202e]/30 focus:ring-[#9c7a3c]"
                        />
                        <span className="text-[#14202e]">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#14202e]">
                    How can Team JurisGo better support you right now in landing a role?
                    <span className="text-xs text-[#14202e]/50 ml-2">(Optional, Private)</span>
                  </label>
                  <Textarea
                    value={formData.support_needed}
                    onChange={(e) => setFormData({ ...formData, support_needed: e.target.value })}
                    placeholder="Share your thoughts on how we can help..."
                    rows={4}
                    className="border-[#14202e]/20 focus:border-[#9c7a3c]"
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#14202e]/10">
              {currentStep > 1 ? (
                <Button 
                  variant="outline" 
                  onClick={prevStep}
                  className="border-[#14202e]/30 text-[#14202e]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> Previous
                </Button>
              ) : (
                <div />
              )}
              
              {(currentStep === 3 || currentStep === 4) ? (
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#9c7a3c] hover:bg-[#8a6b35] text-white"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  <Check className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={nextStep}
                  className="bg-[#14202e] hover:bg-[#1a2a3e] text-white"
                >
                  Continue <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AlumniFeedback;
